import {
  type CasoParaOfertaNotificacao,
  isNotaInternaDoSistema,
} from '@/features/negociacaoDetalhes';
import type {
  AtendimentoDetalheApi,
  AtendimentoListaApi,
  AtendimentoListaItemApi,
  CanalConversa,
  ListarAtendimentosParams,
  MensagemHistoricoApi,
  MensagemHistoricoDaThreadApi,
  MensagemHistoricoItemApi,
  NegociacaoCasoResumoApi,
  TipoVinculoConversa,
} from '@/types/centralAtendimentos/types';
import type { EscritorioMembro } from '@/types/escritorio/escritorioTypes';
import { apiGet, apiPost } from '@/shared/utils/requestBuilder';
import { getStoredWorkspaceId } from '@/shared/utils/workspace';

import type {
  AtendimentoMensagem,
  AtendimentoTab,
  CentralAtendimentosData,
  CentralAtendimentosFiltros,
  DetalheAtendimento,
  NegociacaoAtendimento,
  NotificacaoAtendimento,
  NotificacaoCanal,
  NotificacaoStatus,
  PaginaAtendimentos,
  PaginacaoAtendimentos,
  Responsavel,
  ResponsavelPapel,
  ResumoCasoNegociacao,
} from '../types';
import { formatarDataHora, formatarHoraCurta } from '../utils/atendimentoDatas';

const LIMITE_PAGINA = 50;

const buildQuery = (params: ListarAtendimentosParams): string => {
  const search = new URLSearchParams();
  for (const [chave, valor] of Object.entries(params)) {
    if (valor === undefined || valor === null) continue;
    if (Array.isArray(valor)) {
      search.append(chave, valor.join(','));
      continue;
    }
    search.append(chave, String(valor));
  }
  const query = search.toString();
  return query ? `?${query}` : '';
};

const unwrap = async <T>(response: Response, mensagemErro: string): Promise<T> => {
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const erro = (body as { error?: string; message?: string })?.error;
    const alternativo = (body as { error?: string; message?: string })?.message;
    throw new Error(erro || alternativo || mensagemErro);
  }
  const body = await response.json();
  return (body.data ?? body) as T;
};

const listarAtendimentos = async (
  params: ListarAtendimentosParams,
): Promise<AtendimentoListaApi> => {
  const response = await apiGet(`/conversas${buildQuery(params)}`);
  return unwrap<AtendimentoListaApi>(response, 'Erro ao listar atendimentos');
};

const paramsComuns = (
  filtros: CentralAtendimentosFiltros,
  currentUserId: string | null,
): ListarAtendimentosParams => {
  const params: ListarAtendimentosParams = {
    sort: 'recent',
    limit: LIMITE_PAGINA,
  };

  const busca = filtros.busca.trim();
  if (busca) params.search = busca;

  if (filtros.responsavelFiltro === 'sem') {
    params.semResponsavel = true;
  } else if (filtros.responsavelFiltro === 'meus') {
    if (currentUserId) params.idResponsavel = currentUserId;
  } else if (filtros.responsavelFiltro !== 'todos') {
    params.idResponsavel = filtros.responsavelFiltro;
  }

  return params;
};

const paramsDaAba = (
  tab: AtendimentoTab,
  filtros: CentralAtendimentosFiltros,
  currentUserId: string | null,
  cursor?: string | null,
): ListarAtendimentosParams => ({
  ...paramsComuns(filtros, currentUserId),
  ...(tab === 'negociacoes'
    ? {
        tipoVinculo: 'NEGOCIACAO' as const,
        apenasOrigemAtiva: true,
        // Os dois recortes vão ao servidor: filtrar "sem pendência" na página carregada mostrava
        // só os sem-pendência daqueles 50 itens. `includeTotaisPendencia` traz a contagem dos
        // chips no mesmo pedido — sem ela a contagem saía da página e mudava a cada "carregar
        // mais", e "com pendência" ficava em 0 porque o item só expunha `possuiPendencias`.
        includeTotaisPendencia: true,
        ...(filtros.pendenciaFiltro === 'com' ? { apenasComPendencias: true } : {}),
        ...(filtros.pendenciaFiltro === 'sem' ? { apenasSemPendencias: true } : {}),
      }
    : {
        tipoVinculo: 'NOTIFICACAO_EXTRAJUDICIAL' as const,
        apenasNotificacaoComManifestacao: true,
      }),
  ...(cursor ? { cursor } : {}),
});

const paginacaoDe = (lista: AtendimentoListaApi): PaginacaoAtendimentos => ({
  hasMore: lista.hasMore,
  nextCursor: lista.nextCursor,
});

const isMensagemDaThread = (item: MensagemHistoricoItemApi): item is MensagemHistoricoDaThreadApi =>
  !isNotaInternaDoSistema(item);

const mapMensagem = (item: MensagemHistoricoDaThreadApi): AtendimentoMensagem => ({
  id: item.id,
  remetente: item.direcao === 'INBOUND' ? 'contraparte' : item.senderId ? 'atendente' : 'lexi',
  texto: item.conteudo ?? '',
  // A API atual devolve null quando não há opções; só a ausência da chave é contrato legado.
  metadata: item.metadata === undefined ? item.metadados : item.metadata,
  dataMensagem: item.dataMensagem,
  hora: formatarDataHora(item.dataMensagem),
  tipo: item.tipo,
  status: item.status,
  mediaUrl: item.mediaUrl,
  autorId: item.senderId ?? undefined,
  autorNome: item.senderNome ?? undefined,
});

/**
 * `/mensagens/history` devolve `createdAt desc` (mensagemRepository.listHistory) e a thread é
 * renderizada na ordem do array: sem ordenar, a Central mostrava a conversa INVERTIDA — a mensagem
 * mais nova no topo e a mais antiga no rodapé, onde se lê "última mensagem". Mesma regra que o chat
 * interno aplica em toda escrita da thread.
 */
export const buscarHistoricoAtendimento = async (
  conversaId: string,
): Promise<AtendimentoMensagem[]> => {
  const response = await apiGet('/mensagens/history', { conversaId });
  const historico = await unwrap<MensagemHistoricoApi>(response, 'Erro ao carregar as mensagens');
  return historico.items
    .filter(isMensagemDaThread)
    .sort((a, b) => a.dataMensagem.localeCompare(b.dataMensagem))
    .map(mapMensagem);
};

export const definirResponsavelAtendimento = async (
  conversaId: string,
  responsavelId: string | null,
): Promise<void> => {
  const response = await apiPost(`/conversas/${conversaId}/responsavel`, { responsavelId });
  if (!response.ok) {
    await unwrap<unknown>(response, 'Erro ao definir o responsável do atendimento');
  }
};

export const marcarAtendimentoLido = async (conversaId: string): Promise<void> => {
  const response = await apiPost(`/conversas/${conversaId}/mark-as-read`);
  if (!response.ok) {
    await unwrap<unknown>(response, 'Erro ao marcar o atendimento como lido');
  }
};

const numeroDoVinculo = (item: AtendimentoListaItemApi): string =>
  item.vinculos.find((vinculo) => vinculo.numero)?.numero ?? '';

const entityIdDoVinculo = (
  item: AtendimentoListaItemApi,
  tipo: TipoVinculoConversa,
): string | null => item.vinculos.find((vinculo) => vinculo.tipo === tipo)?.entityId ?? null;

const CANAL_LABEL: Record<CanalConversa, NotificacaoCanal> = {
  WHATSAPP: 'WhatsApp',
  EMAIL: 'E-mail',
  SMS: 'SMS',
  WEB_PORTAL: 'Portal',
  INTERNO: 'Interno',
};

// TODO: AGUARDANDO_ENVIO e EM_NEGOCIACAO não têm rótulo próprio na UI — produto precisa decidir.
const STATUS_NOTIFICACAO: Record<string, NotificacaoStatus> = {
  RASCUNHO: 'Rascunho',
  AGUARDANDO_ENVIO: 'Agendada',
  AGENDADA: 'Agendada',
  EM_PROCESSAMENTO: 'Em processamento',
  ENVIANDO: 'Em processamento',
  ENVIADA: 'Enviada',
  PARCIALMENTE_ENVIADA: 'Enviada',
  REENVIADA: 'Enviada',
  FALHA_ENVIO: 'Falha',
  ENTREGUE: 'Entregue',
  PARCIALMENTE_ENTREGUE: 'Entregue',
  VISUALIZADA: 'Visualizada',
  EM_NEGOCIACAO: 'Enviada',
  CONCLUIDA: 'Concluída',
  CANCELADA: 'Cancelada',
  ARQUIVADA: 'Arquivada',
};

const cpfCnpjDoDetalhe = (detalhe: AtendimentoDetalheApi): string =>
  detalhe.partes.find((parte) => parte.partePrincipal)?.cpfCnpj ?? detalhe.partes[0]?.cpfCnpj ?? '';

const valorDoDetalhe = (detalhe: AtendimentoDetalheApi): number =>
  Number(detalhe.linkedEntity?.valor ?? 0) || 0;

const mapNegociacao = (item: AtendimentoListaItemApi): NegociacaoAtendimento => ({
  casoParaOferta: null,
  id: item.id,
  casoId: entityIdDoVinculo(item, 'NEGOCIACAO'),
  marcadorAtendimento: item.marcadorAtendimento ?? null,
  numero: numeroDoVinculo(item),
  nome: item.nomeCliente ?? '',
  telefone: item.telefoneNormalizado,
  cpfCnpj: '',
  criadoEm: item.createdAt,
  valorEmAberto: 0,
  // Os DOIS eixos, como o filtro do servidor: a pendência de negociação (direcionamento, oferta)
  // mora em `origemComPendencia`, e olhar só `possuiPendencias` marcava todas como sem pendência.
  pendente: item.possuiPendencias || item.origemComPendencia,
  iaPausada: item.marcadorAtendimento === 'IA_PAUSADA',
  encerrada: item.status === 'CLOSED' || item.isArchived,
  arquivada: item.isArchived,
  retomadaAmbigua: item.marcadorAtendimento === 'PRECISA_DIRECAO',
  naoLida: item.unreadCount > 0,
  responsavelId: item.idResponsavel,
  atualizadoEm: item.updatedAt,
  previewTexto: item.ultimaMensagem ?? '',
  previewHora: item.dataUltimaMensagem ? formatarHoraCurta(item.dataUltimaMensagem) : '',
  situacoes: [],
  // A thread vem SÓ de `/mensagens/history`: reconstruir uma mensagem a partir de `ultimaMensagem*`
  // enchia o chat antes do histórico e deixava a última mensagem duplicada ou sem anexo.
  mensagens: [],
  janelaAtendimento: null,
  janelaRestante: item.marcadorAtendimento === 'JANELA_EXPIRADA' ? 'Expirada' : undefined,
});

const mapNotificacao = (item: AtendimentoListaItemApi): NotificacaoAtendimento => ({
  id: item.id,
  itemId: entityIdDoVinculo(item, 'NOTIFICACAO_EXTRAJUDICIAL'),
  numero: numeroDoVinculo(item),
  nome: item.nomeCliente ?? '',
  cpfCnpj: '',
  valor: 0,
  enviadoVia: CANAL_LABEL[item.canal],
  ultimaModificacao: item.updatedAt,
  status: 'Enviada',
  naoLida: item.unreadCount > 0,
  responsavelId: item.idResponsavel,
  respostasSimplesCount: 0,
  mensagensEnviadas: 0,
  atualizadoEm: item.updatedAt,
  previewTexto: item.ultimaMensagem ?? '',
  previewHora: item.dataUltimaMensagem ? formatarHoraCurta(item.dataUltimaMensagem) : '',
  manifestacao: [],
});

const casoParaOfertaDoResumo = (caso: NegociacaoCasoResumoApi): CasoParaOfertaNotificacao => ({
  id: caso.id,
  status: caso.status,
  situacoes: caso.situacoes ?? [],
  notificacaoGeradaItemId: caso.notificacaoGeradaItemId,
  archivedAt: caso.archivedAt,
  atendimentoHumanoAtivo: caso.atendimentoHumanoAtivo,
  encerramento: caso.encerramento,
  counterpartyName: caso.counterpartyName,
  counterpartyCpfCnpj: caso.counterpartyCpfCnpj,
  counterpartyEmail: caso.counterpartyEmail,
  counterpartyPhone: caso.counterpartyPhone,
  claimAmount: caso.claimAmount,
});

/**
 * Valor e situações são do CASO, não da conversa: o vínculo NEGOCIACAO não produz `linkedEntity`
 * em `/conversas/:id/details`, então o painel lia `valor` de um objeto nulo — R$ 0,00 em toda
 * negociação — e não tinha de onde tirar a situação. A revalidação de fundo do atendimento aberto
 * chama só esta consulta: `/details` (partes, CPF/CNPJ) não muda entre um ciclo e outro.
 */
export const buscarResumoDoCaso = async (casoId: string): Promise<ResumoCasoNegociacao> => {
  const response = await apiGet(`/negociacao/casos/${casoId}`);
  const body = await unwrap<{ caso?: NegociacaoCasoResumoApi }>(
    response,
    'Erro ao carregar a negociação',
  );
  return {
    casoParaOferta: body.caso ? casoParaOfertaDoResumo(body.caso) : null,
    valor: body.caso?.claimAmount ?? 0,
    situacoes: (body.caso?.situacoes ?? []).map((situacao) => ({
      id: situacao.id,
      key: situacao.key,
      label: situacao.label,
    })),
    janelaAtendimento: body.caso?.janelaAtendimento ?? null,
  };
};

/**
 * `/details`, não `/conversas/:id`: só ele resolve `partes` e `linkedEntity`. A rota sem sufixo
 * devolve a conversa crua, então CPF/CNPJ e valor chegavam sempre vazios no painel. Falha no caso
 * vira null e o painel mantém CPF/CNPJ e status do detalhe, em vez de perder o painel inteiro.
 */
export const buscarDetalheAtendimento = async (conversaId: string): Promise<DetalheAtendimento> => {
  const response = await apiGet(`/conversas/${conversaId}/details`);
  const detalhe = await unwrap<AtendimentoDetalheApi>(response, 'Erro ao carregar o atendimento');
  const statusBruto = detalhe.linkedEntity?.status;
  const casoId = entityIdDoVinculo(detalhe, 'NEGOCIACAO');
  const caso = casoId ? await buscarResumoDoCaso(casoId).catch(() => null) : null;
  return {
    cpfCnpj: cpfCnpjDoDetalhe(detalhe),
    valor: caso?.valor ?? valorDoDetalhe(detalhe),
    status: statusBruto ? (STATUS_NOTIFICACAO[statusBruto] ?? null) : null,
    situacoes: caso?.situacoes ?? [],
    casoParaOferta: caso?.casoParaOferta ?? null,
    janelaAtendimento: caso ? caso.janelaAtendimento : undefined,
  };
};

const PAPEL_POR_ESCRITORIO: Record<string, ResponsavelPapel> = {
  MASTER_ADMIN: 'Administrador',
  ADMIN: 'Administrador',
  EDITOR: 'Editor',
  VIEWER: 'Visualizador',
};

const listarResponsaveis = async (): Promise<Responsavel[]> => {
  const escritorioId = getStoredWorkspaceId();
  if (!escritorioId) return [];
  const response = await apiGet(`/escritorios/${escritorioId}/membros?ativo=true`);
  const membros = await unwrap<EscritorioMembro[]>(response, 'Erro ao listar responsáveis');
  return membros.map((membro) => ({
    id: membro.usuarioId,
    nome: membro.usuario.nomeCompleto,
    papel: PAPEL_POR_ESCRITORIO[membro.papel] ?? 'Visualizador',
  }));
};

/**
 * Carrega SÓ a aba ativa. Antes buscava as duas a cada mudança de filtro, dobrando o tráfego para
 * alimentar uma lista que a tela nem mostra — e as abas não exibem contador, então nada na UI
 * dependia da outra. Quem troca de aba dispara um fetch novo, porque `tipoAtivo` está nos filtros.
 */
export const fetchCentralAtendimentosData = async (
  filtros: CentralAtendimentosFiltros,
  currentUserId: string | null,
): Promise<CentralAtendimentosData> => {
  const tab: AtendimentoTab = filtros.tipoAtivo === 'notificacoes' ? 'notificacoes' : 'negociacoes';
  const [responsaveis, lista] = await Promise.all([
    listarResponsaveis(),
    listarAtendimentos(paramsDaAba(tab, filtros, currentUserId)),
  ]);

  return {
    tab,
    responsaveis,
    negociacoes: tab === 'negociacoes' ? lista.items.map(mapNegociacao) : [],
    notificacoes: tab === 'notificacoes' ? lista.items.map(mapNotificacao) : [],
    paginacao: paginacaoDe(lista),
    totaisPendencia: lista.totaisPendencia ?? null,
  };
};

export const carregarPaginaAtendimentos = async (
  tab: AtendimentoTab,
  filtros: CentralAtendimentosFiltros,
  currentUserId: string | null,
  cursor: string,
): Promise<PaginaAtendimentos> => {
  const lista = await listarAtendimentos(paramsDaAba(tab, filtros, currentUserId, cursor));

  return {
    tipo: tab,
    cursorUsado: cursor,
    negociacoes: tab === 'negociacoes' ? lista.items.map(mapNegociacao) : [],
    notificacoes: tab === 'notificacoes' ? lista.items.map(mapNotificacao) : [],
    paginacao: paginacaoDe(lista),
  };
};
